import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import useApi from '../../hooks/useApi';
import { useErrorToast } from '../../components/common';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { fetchAiModelPricingHistory } from '../../services/aiGovernanceService';
import { formatCost } from '../../constants/aiUsage';

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '—';
  }
};

/**
 * Every price a model has ever had, newest first.
 *
 * Prices are effective-dated rather than overwritten, so an invoice raised last
 * quarter can always be reconciled against the rate that applied then.
 */
const AiModelPricingHistoryDialog = ({ open, model, onClose }) => {
  const api = useApi();
  const { showErrorToast } = useErrorToast();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const modelCode = model?.modelCode;

  const load = useCallback(async () => {
    if (!modelCode) return;

    setLoading(true);
    try {
      setHistory(await fetchAiModelPricingHistory(api, modelCode));
    } catch (err) {
      console.error('Error loading pricing history:', err);
      showErrorToast(getApiErrorMessage(err, 'Failed to load pricing history'));
    } finally {
      setLoading(false);
    }
  }, [api, modelCode, showErrorToast]);

  useEffect(() => {
    if (open && modelCode) {
      load();
    } else if (!open) {
      setHistory([]);
    }
  }, [open, modelCode, load]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Pricing history — {model?.modelName || modelCode}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Prices per 1,000,000 tokens
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : history.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No pricing has been set for this model yet.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Input</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Output</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Effective from</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Effective to</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Set by</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row.aiModelPricingId} hover>
                    <TableCell>{formatCost(row.inputPricePerMillion, row.currency)}</TableCell>
                    <TableCell>{formatCost(row.outputPricePerMillion, row.currency)}</TableCell>
                    <TableCell>{formatDateTime(row.effectiveFrom)}</TableCell>
                    <TableCell>
                      {row.isCurrent ? (
                        <Chip label="Current" size="small" color="success" variant="outlined" />
                      ) : (
                        formatDateTime(row.effectiveTo)
                      )}
                    </TableCell>
                    <TableCell>{row.createdBy || 'system'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiModelPricingHistoryDialog;
